'use client';
import React, { useEffect, useRef, useState } from 'react';
import { storyAt, type Story } from '@/lib/presentation/story';
import { storyTime, usePresentation } from '@/lib/presentation/store';

interface Props {
  index: number;
  steps: string[];
  story: Story;
}

/**
 * Compact step timeline for a scene. It reads the same story clock as the 3D station, so
 * the highlighted step always matches what is happening in the world.
 */
export function StoryProgress({ index, steps, story }: Props) {
  const active = usePresentation((s) => s.index === index);
  const reduced = usePresentation((s) => s.reducedMotion);
  const [step, setStep] = useState(-1);
  const bar = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const setBar = (p: number) => {
      if (bar.current) bar.current.style.transform = `scaleX(${p})`;
    };
    if (!active) {
      setStep(-1);
      setBar(0);
      return;
    }
    if (reduced) {
      setStep(steps.length - 1);
      setBar(1);
      return;
    }
    let raf = 0;
    let current = -2;
    const tick = () => {
      const s = storyAt(storyTime(), story);
      if (s.step !== current) {
        current = s.step;
        setStep(s.step);
      }
      setBar(s.progress);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, reduced, story, steps.length]);

  const shown = Math.max(0, step);

  return (
    <div className="v2-story">
      <p className="v2-story-count" aria-hidden="true">
        {String(shown + 1).padStart(2, '0')}
        <span className="text-white/35"> / {String(steps.length).padStart(2, '0')}</span>
      </p>
      <ol className="v2-story-steps" aria-label="How it works, step by step">
        {steps.map((label, i) => (
          <li
            key={label}
            data-state={i < step ? 'done' : i === step ? 'active' : 'todo'}
            aria-current={i === step ? 'step' : undefined}
          >
            <span className="v2-story-dot" aria-hidden="true" />
            {label}
          </li>
        ))}
      </ol>
      <span className="v2-story-track" aria-hidden="true">
        <span ref={bar} className="v2-story-bar" />
      </span>
    </div>
  );
}
